<template>
  <div class="regions-configs-container">
    <!-- Cabeçalho da página -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-cog"></i>
        Configurações da Região
      </h1>
      <p class="page-subtitle">Configure os parâmetros da região selecionada</p>

      <!-- Copiar configurações de outra região (mesma banca, ou de outra banca no caso do ADMIN) -->
      <div v-if="canEdit && (isAdmin ? allBancasForCopy.length > 0 : bancaRegionsToCopy.length > 0)" class="copy-configs-card">
        <div class="copy-configs-header">
          <i class="fa-solid fa-copy"></i>
          <div>
            <strong>Copiar configurações de outra região</strong>
            <p v-if="isAdmin">Copia loterias, modalidades, cotação (Lotinha/Quininha/Seninha), limites e configurações de prêmio. Escolha a banca de origem e a região dela para copiar.</p>
            <p v-else>Copia loterias, modalidades, cotação (Lotinha/Quininha/Seninha), limites e configurações de prêmio. Aparecem as mesmas regiões disponíveis no seletor do header.</p>
          </div>
        </div>
        <div class="copy-configs-actions">
          <div v-if="isAdmin && allBancasForCopy.length > 1" class="copy-region-select-wrapper">
            <select
              class="form-input-modern copy-region-select"
              v-model="copyFromBancaId"
              :disabled="copyingConfigs"
              @change="onCopyFromBancaChange"
            >
              <option :value="null">Selecione a banca de origem...</option>
              <option
                v-for="banca in allBancasForCopy"
                :key="banca.id"
                :value="banca.id"
              >
                {{ banca.name }}
              </option>
            </select>
          </div>
          <div class="copy-region-select-wrapper">
            <select
              class="form-input-modern copy-region-select"
              v-model="copyFromRegionId"
              :disabled="copyingConfigs"
            >
              <option :value="null">Selecione a região de origem...</option>
              <option
                v-for="region in bancaRegionsToCopy"
                :key="region.id"
                :value="region.id"
              >
                {{ region.name }}
              </option>
            </select>
          </div>
          <button
            type="button"
            class="btn-copy-configs"
            :disabled="!copyFromRegionId || copyingConfigs"
            @click="copyConfigsFromRegion"
          >
            <i :class="copyingConfigs ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-clone'"></i>
            {{ copyingConfigs ? 'Copiando...' : 'Copiar agora' }}
          </button>
        </div>
      </div>
      
      <!-- Alerta de modo somente leitura -->
      <div v-if="isReadOnly" class="alert alert-warning mt-3">
        <i class="fa-solid fa-eye"></i>
        <strong>Modo Somente Leitura:</strong> 
        <span v-if="isCobrador || isGerente">Você pode visualizar todas as configurações, mas não pode editá-las.</span>
        <span v-else-if="isBanca">Você não tem permissão para editar as configurações desta região.</span>
      </div>
    </div>

    <!-- Accordion moderno -->
    <div class="accordion-modern" :class="{ 'limits-open': openLimits }">
      <!-- Loterias -->
      <div class="accordion-item-modern">
        <button class="accordion-header-modern" @click="openLotteries = !openLotteries; preserveAccordionState()">
          <h3 class="accordion-title">
            <i class="fa-solid fa-trophy"></i>
            Loterias
          </h3>
          <i class="fa-solid fa-chevron-down accordion-toggle" :class="{ 'rotated': openLotteries }"></i>
        </button>
        <div class="accordion-collapse" :class="{ 'show': openLotteries }">
          <div class="accordion-body-modern">
            <div v-if="loading" class="configs-loading">
              <i class="fa-solid fa-spinner fa-spin"></i>
              <span>Carregando configurações...</span>
            </div>
            <div v-else>
              <RegionsConfigsLotteries 
                ref="lotteriesComponent"
                :regionLotteriesParameter="regionParameter.regionLotteriesParameter" 
                :canEdit="canEdit"
                :isReadOnly="isReadOnly"
                @save-changes="saveRegionConfigs"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Modalidades -->
      <div class="accordion-item-modern">
        <button class="accordion-header-modern" @click="openModalities = !openModalities; preserveAccordionState()">
          <h3 class="accordion-title">
            <i class="fa-solid fa-gamepad"></i>
            Modalidades
          </h3>
          <i class="fa-solid fa-chevron-down accordion-toggle" :class="{ 'rotated': openModalities }"></i>
        </button>
        <div class="accordion-collapse" :class="{ 'show': openModalities }">
          <div class="accordion-body-modern">
            <div v-if="loading" class="configs-loading">
              <i class="fa-solid fa-spinner fa-spin"></i>
              <span>Carregando configurações...</span>
            </div>
            <div v-else>
              <RegionsConfigsModalities
                ref="modalitiesComponent"
                :regionModalitiesParameter="regionParameter.regionModalitiesParameter"
                :canEdit="canEdit"
                :isReadOnly="isReadOnly"
                @addModality="receiveAddModality"
                @save-changes="saveRegionConfigs"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Cotação Lotinha / Quininha / Seninha -->
      <div class="accordion-item-modern">
        <button class="accordion-header-modern" @click="openLotteryCotacao = !openLotteryCotacao; preserveAccordionState()">
          <h3 class="accordion-title">
            <i class="fa-solid fa-ticket"></i>
            Cotação Loteria (Lotinha / Quininha / Seninha / Bingo / Loto Quina)
          </h3>
          <i class="fa-solid fa-chevron-down accordion-toggle" :class="{ 'rotated': openLotteryCotacao }"></i>
        </button>
        <div class="accordion-collapse" :class="{ 'show': openLotteryCotacao }">
          <div class="accordion-body-modern">
            <RegionsConfigsLotteryCotacao
              :key="cotacaoRefreshKey"
              :canEdit="canEdit"
              :isReadOnly="isReadOnly"
            />
          </div>
        </div>
      </div>

      <!-- Limites -->
      <div class="accordion-item-modern">
        <button class="accordion-header-modern" @click="openLimits = !openLimits; preserveAccordionState()">
          <h3 class="accordion-title">
            <i class="fa-solid fa-hand-paper"></i>
            Limites
          </h3>
          <i class="fa-solid fa-chevron-down accordion-toggle" :class="{ 'rotated': openLimits }"></i>
        </button>
        <div class="accordion-collapse" :class="{ 'show': openLimits }">
          <div class="accordion-body-modern">
            <div v-if="loading" class="configs-loading">
              <i class="fa-solid fa-spinner fa-spin"></i>
              <span>Carregando configurações...</span>
            </div>
            <div v-else>
              <RegionsConfigsLimits
                :regionLimitsParameter="regionParameter.regionLotteriesModalitiesLimitsParameter"
                :regionLotteriesParameter="regionParameter.regionLotteriesParameter"
                :canEdit="canEdit"
                :isReadOnly="isReadOnly"
                @field-changed="autoSave"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Descargas (oculto por enquanto)
      <div class="accordion-item-modern">
        <button class="accordion-header-modern" @click="openExhausts = !openExhausts; preserveAccordionState()">
          <h3 class="accordion-title">
            <i class="fa-solid fa-download"></i>
            Descargas
          </h3>
          <i class="fa-solid fa-chevron-down accordion-toggle" :class="{ 'rotated': openExhausts }"></i>
        </button>
        <div class="accordion-collapse" :class="{ 'show': openExhausts }">
          <div class="accordion-body-modern">
            <div v-if="loading" class="configs-loading">
              <i class="fa-solid fa-spinner fa-spin"></i>
              <span>Carregando configurações...</span>
            </div>
            <div v-else>
              <RegionsConfigsExhausts 
                :regionExhausts="regionParameter.regionExhaustParameter" 
                :canEdit="canEdit"
                :isReadOnly="isReadOnly"
                @field-changed="autoSave"
              />
            </div>
          </div>
        </div>
      </div>
      -->

      <!-- Configurações de Prêmio -->
      <div class="accordion-item-modern">
        <button class="accordion-header-modern" @click="openGeneralSettings = !openGeneralSettings; preserveAccordionState()">
          <h3 class="accordion-title">
            <i class="fa-solid fa-sliders"></i>
            Configurações de Prêmio
          </h3>
          <i class="fa-solid fa-chevron-down accordion-toggle" :class="{ 'rotated': openGeneralSettings }"></i>
        </button>
        <div class="accordion-collapse" :class="{ 'show': openGeneralSettings }">
          <div class="accordion-body-modern">
            <div v-if="loading" class="configs-loading">
              <i class="fa-solid fa-spinner fa-spin"></i>
              <span>Carregando configurações...</span>
            </div>
            <div v-else>
              <div class="general-settings-container">
                <div class="setting-item">
                  <div class="setting-header">
                    <label class="setting-label">
                      <i class="fa-solid fa-calendar-days"></i>
                      Dias para Expiração do Prêmio
                    </label>
                    <div class="setting-description">
                      Define quantos dias após o sorteio o bilhete premiado pode ser resgatado
                    </div>
                  </div>
                  <div class="setting-input">
                    <input 
                      type="number" 
                      class="form-input-modern" 
                      v-model.number="prizeExpirationDays"
                      @input="savePrizeSettings"
                      :disabled="isReadOnly"
                      :readonly="isReadOnly"
                      min="1"
                      max="365"
                      placeholder="Digite o número de dias (1-365)"
                    />
                    <span class="input-suffix">dias</span>
                  </div>
                </div>

                <div class="setting-item">
                  <div class="setting-header">
                    <label class="setting-label">
                      <i class="fa-solid fa-key"></i>
                      Exigir PIN para Resgate de Prêmio
                    </label>
                    <div class="setting-description">
                      Ativado: o prêmio só é liberado após resgate com PIN.
                      Desativado: ao premiar o bilhete, o pagamento já é liberado na carteira do vendedor.
                    </div>
                  </div>
                  <div class="setting-toggle">
                    <label class="toggle-switch" :class="{ 'disabled': isReadOnly }">
                      <input
                        type="checkbox"
                        v-model="requirePinForPrizeClaim"
                        @change="savePrizeSettings"
                        :disabled="isReadOnly"
                      />
                      <span class="toggle-slider"></span>
                    </label>
                    <span class="toggle-status">
                      {{ requirePinForPrizeClaim ? 'Ativado — PIN obrigatório' : 'Desativado — pagamento automático' }}
                    </span>
                  </div>
                </div>

                <div class="setting-item milhar-bonus-setting">
                  <div class="setting-header">
                    <label class="setting-label">
                      <i class="fa-solid fa-gift"></i>
                      Milhar de Bônus
                    </label>
                    <div class="setting-description">
                      Ativado: no fim de cada bilhete gera um milhar (1º prêmio). Não entra em venda, comissão nem descarga — só na contabilidade se for premiado.
                      Configure abaixo o mínimo para liberar, valor máximo da linha, cotação e prêmio máximo.
                    </div>
                  </div>
                  <div class="milhar-bonus-row">
                    <div class="setting-toggle">
                      <label class="toggle-switch" :class="{ 'disabled': isReadOnly }">
                        <input
                          type="checkbox"
                          v-model="milharBonusEnabled"
                          @change="savePrizeSettings"
                          :disabled="isReadOnly"
                        />
                        <span class="toggle-slider"></span>
                      </label>
                      <span class="toggle-status">
                        {{ milharBonusEnabled ? 'Ativado' : 'Desativado' }}
                      </span>
                    </div>
                  </div>
                  <div
                    v-if="milharBonusEnabled"
                    class="milhar-bonus-fields"
                  >
                    <label class="milhar-field-label">
                      Mín. p/ liberar (R$)
                      <input
                        type="number"
                        class="milhar-field-input"
                        v-model.number="milharBonusMinValue"
                        @input="savePrizeSettings"
                        :disabled="isReadOnly"
                        :readonly="isReadOnly"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                      />
                    </label>
                    <label class="milhar-field-label">
                      Valor máx. aposta (R$)
                      <input
                        type="number"
                        class="milhar-field-input"
                        v-model.number="milharBonusMaxBetValue"
                        @input="savePrizeSettings"
                        :disabled="isReadOnly"
                        :readonly="isReadOnly"
                        min="0.01"
                        step="0.01"
                        placeholder="10.00"
                      />
                    </label>
                    <label class="milhar-field-label">
                      Cotação (x)
                      <input
                        type="number"
                        class="milhar-field-input"
                        v-model.number="milharBonusCotacao"
                        @input="savePrizeSettings"
                        :disabled="isReadOnly"
                        :readonly="isReadOnly"
                        min="0.01"
                        step="1"
                        placeholder="100"
                      />
                    </label>
                    <label class="milhar-field-label">
                      Prêmio máximo (R$)
                      <input
                        type="number"
                        class="milhar-field-input"
                        v-model.number="milharBonusMaxPrize"
                        @input="savePrizeSettings"
                        :disabled="isReadOnly"
                        :readonly="isReadOnly"
                        min="0.01"
                        step="0.01"
                        placeholder="1000.00"
                      />
                    </label>
                  </div>
                </div>

                <div class="setting-item milhar-bonus-setting">
                  <div class="setting-header">
                    <label class="setting-label">
                      <i class="fa-solid fa-image"></i>
                      Imagem no rodapé do bilhete
                    </label>
                    <div class="setting-description">
                      Ativado: imprime uma imagem e um texto opcional abaixo de “Prêmio válido por X dias”.
                      Por padrão fica desativado.
                    </div>
                  </div>
                  <div class="milhar-bonus-row">
                    <div class="setting-toggle">
                      <label class="toggle-switch" :class="{ 'disabled': isReadOnly }">
                        <input
                          type="checkbox"
                          v-model="ticketFooterEnabled"
                          @change="savePrizeSettings"
                          :disabled="isReadOnly"
                        />
                        <span class="toggle-slider"></span>
                      </label>
                      <span class="toggle-status">
                        {{ ticketFooterEnabled ? 'Ativado' : 'Desativado' }}
                      </span>
                    </div>
                  </div>
                  <div
                    v-if="ticketFooterEnabled"
                    class="ticket-footer-fields"
                  >
                    <div class="ticket-footer-image-block">
                      <span class="ticket-footer-field-title">Imagem</span>
                      <div
                        class="ticket-footer-dropzone"
                        :class="{
                          'has-image': !!ticketFooterImage,
                          disabled: isReadOnly,
                          loading: ticketFooterImageLoading,
                        }"
                        @click="!isReadOnly && !ticketFooterImageLoading && $refs.ticketFooterFileInput?.click()"
                        @dragover.prevent
                        @drop.prevent="onTicketFooterImageDrop"
                      >
                        <img
                          v-if="ticketFooterImage && !ticketFooterImageLoading"
                          :src="ticketFooterImage"
                          alt="Prévia da imagem do rodapé"
                          class="ticket-footer-preview-img"
                        />
                        <div v-else-if="ticketFooterImageLoading" class="ticket-footer-placeholder">
                          <i class="fa-solid fa-spinner fa-spin"></i>
                          <span>Processando imagem...</span>
                        </div>
                        <div v-else class="ticket-footer-placeholder">
                          <i class="fa-solid fa-cloud-arrow-up"></i>
                          <span>Clique ou arraste uma imagem aqui</span>
                          <small>PNG, JPG ou WEBP — prévia aparece abaixo do upload</small>
                        </div>
                      </div>
                      <input
                        ref="ticketFooterFileInput"
                        type="file"
                        class="ticket-footer-file-input"
                        accept="image/png,image/jpeg,image/webp,image/*"
                        :disabled="isReadOnly || ticketFooterImageLoading"
                        @change="onTicketFooterImageSelected"
                      />
                      <div class="ticket-footer-image-actions">
                        <button
                          type="button"
                          class="ticket-footer-upload-btn"
                          :disabled="isReadOnly || ticketFooterImageLoading"
                          @click="$refs.ticketFooterFileInput?.click()"
                        >
                          <i class="fa-solid fa-upload"></i>
                          {{ ticketFooterImage ? 'Trocar imagem' : 'Selecionar imagem' }}
                        </button>
                        <button
                          v-if="ticketFooterImage && !isReadOnly"
                          type="button"
                          class="ticket-footer-remove-btn"
                          :disabled="ticketFooterImageLoading"
                          @click="clearTicketFooterImage"
                        >
                          <i class="fa-solid fa-trash"></i>
                          Remover
                        </button>
                      </div>
                      <small class="ticket-footer-hint">
                        A imagem é redimensionada automaticamente e salva na região.
                      </small>
                    </div>
                    <label class="milhar-field-label ticket-footer-text-label">
                      Texto abaixo da imagem
                      <input
                        type="text"
                        class="milhar-field-input"
                        v-model="ticketFooterText"
                        @input="savePrizeSettings"
                        :disabled="isReadOnly"
                        :readonly="isReadOnly"
                        maxlength="512"
                        placeholder="Ex.: WhatsApp (11) 99999-9999"
                      />
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import { ROLES } from "@/services/permissions";
import RegionsConfigsModalities from "./RegionsConfigsModalities.vue";
import RegionsConfigsLotteries from "./RegionsConfigsLotteries.vue";
import RegionsConfigsLimits from "./RegionsConfigsLimits.vue";
// import RegionsConfigsExhausts from "./RegionsConfigsExhausts.vue"; // oculto por enquanto
import RegionsConfigsLotteryCotacao from "./RegionsConfigsLotteryCotacao.vue";
export default {
  components: {
    RegionsConfigsModalities,
    RegionsConfigsLotteries,
    RegionsConfigsLimits,
    // RegionsConfigsExhausts, // oculto por enquanto
    RegionsConfigsLotteryCotacao,
  },
  data: () => ({
    openLotteries: true,
    openModalities: false,
    openLimits: false,
    openExhausts: false,
    openLotteryCotacao: false,
    openGeneralSettings: false,
    regionParameter: {
      regionModalitiesParameter: [],
      regionLotteriesParameter: [],
      regionLotteriesModalitiesLimitsParameter: [],
      regionExhaustParameter: []
    },
    regionParameter2: {},
    loading: true,
    saving: false,
    currentUser: null,
    userResponsibleRegions: [], // Array das regiões pelas quais o usuário é responsável
    userBancaId: null, // BancaId do usuário BANCA
    prizeExpirationDays: 7, // Dias para expiração do prêmio
    requirePinForPrizeClaim: true, // Exigir PIN no resgate (padrão = comportamento atual)
    milharBonusEnabled: false, // Milhar de bônus (padrão off)
    milharBonusMinValue: 0, // Valor mínimo da aposta para liberar
    milharBonusMaxBetValue: 10, // Valor máximo da linha de bônus
    milharBonusCotacao: 100, // Multiplicador
    milharBonusMaxPrize: 1000, // Prêmio máximo
    ticketFooterEnabled: false, // Imagem/texto no rodapé do bilhete (padrão off)
    ticketFooterImage: null,
    ticketFooterText: '',
    ticketFooterImageLoading: false,
    currentRegionData: null, // Dados completos da região atual
    autoSaveTimeout: null, // Timer para debounce do auto-save
    prizeSettingsTimeout: null, // Timer debounce configs de prêmio
    bancaRegionsToCopy: [],
    copyFromRegionId: null,
    allBancasForCopy: [], // ADMIN: todas as bancas, para copiar configs de região de outra banca
    copyFromBancaId: null, // ADMIN: banca de origem selecionada (padrão = banca atual)
    copyingConfigs: false,
    cotacaoRefreshKey: 0,
  }),
  computed: {
    isCobrador() {
      const role = parseInt(this.currentUser?.role);
      return this.currentUser && role === ROLES.COBRADOR;
    },
    
    isGerente() {
      const role = parseInt(this.currentUser?.role);
      return this.currentUser && role === ROLES.GERENTE;
    },
    
    isBanca() {
      const role = parseInt(this.currentUser?.role);
      return this.currentUser && role === ROLES.BANCA;
    },
    
    isAdmin() {
      const role = parseInt(this.currentUser?.role);
      return this.currentUser && role === ROLES.ADMIN;
    },
    
    // Verifica se o usuário pode editar as configurações
    canEdit() {
      if (this.isAdmin) {
        return true; // ADMIN: acesso total
      }
      
      if (this.isBanca && this.currentRegion) {
        // BANCA: pode editar apenas se a região atual está nas suas regiões responsáveis
        return this.userResponsibleRegions.includes(this.currentRegion.id);
      }
      
      // GERENTE e COBRADOR: apenas visualização (não podem editar)
      return false;
    },
    
    // Indica se é apenas modo de visualização
    isReadOnly() {
      return !this.canEdit;
    },
    
    // Nova computed property para verificar se pode visualizar os campos
    canView() {
      if (this.isAdmin) {
        return true; // ADMIN: pode ver e editar tudo
      }
      
      if ((this.isGerente || this.isBanca) && this.currentRegion) {
        // GERENTE e BANCA: podem ver apenas se a região atual está nas suas regiões responsáveis
        return this.userResponsibleRegions.includes(this.currentRegion.id);
      }
      
      if (this.isCobrador) {
        // COBRADOR: pode ver tudo mas não editar
        return true;
      }
      
      return false;
    }
  },
  async mounted() {
    this.loading = true;
    this.currentUser = JSON.parse(localStorage.getItem("user"));
    this.currentRegion = JSON.parse(localStorage.getItem("currentRegion"));
    
    // Restaurar estado dos accordions
    this.restoreAccordionState();
    
    try {
      // Carregar regiões responsáveis do usuário ANTES de verificar permissões
      if (this.isBanca || this.isGerente || this.isCobrador) {
        await this.loadUserResponsibleRegions();
      }
      
      // Aguardar um ciclo do Vue para garantir que as computed properties sejam atualizadas
      await this.$nextTick();
      
      if (this.currentRegion) {
        // Verificar se o usuário tem permissão para acessar esta região
        if (this.canAccessCurrentRegion()) {
          await this.loadCurrentRegionData();
          await this.loadBancaRegionsToCopy();
          if (this.isAdmin) {
            await this.loadAllBancasForCopy();
          }
          this.searchRegionConfigs();
        } else {
          this.loading = false;
          toast.error("Você não tem permissão para acessar as configurações desta região!");
        }
      } else {
        this.loading = false;
        toast.error("Selecione uma região!");
      }
    } catch (error) {
      this.loading = false;
      toast.error(handleErrorMessage(error));
    }
  },
  methods: {
    async loadBancaRegionsToCopy() {
      try {
        this.bancaRegionsToCopy = [];
        this.copyFromRegionId = null;

        if (!this.currentUser?.id || !this.currentRegion?.id) {
          return;
        }

        // Mesma lógica do seletor de região do header (MainHeader.loadRegions)
        let availableRegions = [];

        if (this.isAdmin) {
          // ADMIN: regiões da banca selecionada no header
          let banca = null;
          const selectedBanca = localStorage.getItem("selectedBanca");
          if (selectedBanca) {
            try {
              banca = JSON.parse(selectedBanca);
            } catch (e) {
              banca = null;
            }
          }

          // Fallback: banca da região atual
          if (!banca?.id && this.currentRegionData?.bancaId) {
            banca = { id: this.currentRegionData.bancaId };
          }

          const allRegionsResponse = await mainService.searchRegions();
          const allRegions = allRegionsResponse.data || [];

          this.copyFromBancaId = banca?.id || null;

          if (banca?.id) {
            // ADMIN: todas as regiões da banca selecionada no header (default; pode trocar no seletor de banca)
            availableRegions = allRegions.filter(region => region.bancaId === banca.id);
          } else {
            availableRegions = [];
          }
        } else if (this.isBanca) {
          // BANCA: regiões da própria banca
          let bancaId = this.userBancaId;
          if (!bancaId) {
            const bancaResponse = await mainService.getUserBancaId(this.currentUser.id);
            bancaId = bancaResponse.data;
            this.userBancaId = bancaId;
          }

          const allRegionsResponse = await mainService.searchRegions();
          const allRegions = allRegionsResponse.data || [];
          availableRegions = bancaId
            ? allRegions.filter(region => region.bancaId === bancaId)
            : [];
        } else {
          // GERENTE/COBRADOR: regiões vinculadas ao usuário
          const [userRegionsResponse, allRegionsResponse] = await Promise.all([
            mainService.getUserRegions(this.currentUser.id),
            mainService.searchRegions()
          ]);

          const userRegions = userRegionsResponse.data || [];
          const allRegions = allRegionsResponse.data || [];
          const regionIds = userRegions.map(r => r.id);

          if (this.currentUser.regionId && !regionIds.includes(this.currentUser.regionId)) {
            regionIds.push(this.currentUser.regionId);
          }

          availableRegions = allRegions.filter(region => regionIds.includes(region.id));
        }

        this.bancaRegionsToCopy = availableRegions
          .filter(region => region.id !== this.currentRegion.id)
          .sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR"));
      } catch (error) {
        console.error("Erro ao carregar regiões para cópia:", error);
        this.bancaRegionsToCopy = [];
      }
    },

    // ADMIN: lista de todas as bancas, para poder copiar configs de região de uma banca diferente da atual
    async loadAllBancasForCopy() {
      try {
        const response = await mainService.searchBancas({ isActive: true });
        this.allBancasForCopy = (response.data || [])
          .slice()
          .sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR"));
      } catch (error) {
        console.error("Erro ao carregar bancas para cópia:", error);
        this.allBancasForCopy = [];
      }
    },

    // ADMIN: troca a banca de origem no seletor -> recarrega as regiões dessa banca
    async onCopyFromBancaChange() {
      this.copyFromRegionId = null;

      if (!this.copyFromBancaId) {
        this.bancaRegionsToCopy = [];
        return;
      }

      try {
        const allRegionsResponse = await mainService.searchRegions();
        const allRegions = allRegionsResponse.data || [];

        this.bancaRegionsToCopy = allRegions
          .filter(region => region.bancaId === this.copyFromBancaId && region.id !== this.currentRegion.id)
          .sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR"));
      } catch (error) {
        console.error("Erro ao carregar regiões da banca selecionada:", error);
        this.bancaRegionsToCopy = [];
      }
    },

    async copyConfigsFromRegion() {
      if (!this.canEdit) {
        toast.error("Você não tem permissão para editar as configurações desta região!");
        return;
      }

      if (!this.copyFromRegionId || !this.currentRegion?.id) {
        toast.error("Selecione uma região de origem!");
        return;
      }

      const sourceRegion = this.bancaRegionsToCopy.find(r => r.id === this.copyFromRegionId);
      const sourceName = sourceRegion?.name || "região selecionada";
      const sourceBanca = this.isAdmin
        ? this.allBancasForCopy.find(b => b.id === this.copyFromBancaId)
        : null;
      const sourceLabel = sourceBanca ? `"${sourceName}" (banca ${sourceBanca.name})` : `"${sourceName}"`;

      this.$confirm({
        title: "Copiar configurações",
        message: `Confirma copiar de ${sourceLabel} as loterias, modalidades, cotação (Lotinha/Quininha/Seninha), limites e configurações de prêmio? Esta ação substitui as configs atuais.`,
        button: {
          no: "Cancelar",
          yes: "Copiar",
        },
        callback: async (confirm) => {
          if (!confirm) return;

          this.copyingConfigs = true;
          try {
            await mainService.copyRegionConfigs(this.copyFromRegionId, this.currentRegion.id);
            toast.success("Configurações copiadas com sucesso!");
            this.copyFromRegionId = null;
            this.cotacaoRefreshKey += 1;
            await this.loadCurrentRegionData();
            this.searchRegionConfigs(false);
          } catch (error) {
            toast.error(handleErrorMessage(error));
          } finally {
            this.copyingConfigs = false;
          }
        },
      });
    },

    async loadUserResponsibleRegions() {
      try {
        if (this.currentUser && this.currentUser.id) {
          if (this.isBanca) {
            // Para BANCA: buscar o BancaId do usuário e depois filtrar regiões
            const bancaResponse = await mainService.getUserBancaId(this.currentUser.id);
            this.userBancaId = bancaResponse.data;

            if (this.userBancaId) {
              // Buscar todas as regiões e filtrar por BancaId
              const allRegionsResponse = await mainService.searchRegions();
              const allRegions = allRegionsResponse.data || [];
              
              // Filtrar regiões que pertencem à banca do usuário
              const bancaRegions = allRegions.filter(region => region.bancaId === this.userBancaId);
              this.userResponsibleRegions = bancaRegions.map(region => region.id);
            } else {
              console.error('Usuário BANCA não possui BancaId definido');
              this.userResponsibleRegions = [];
            }
          } else if (this.isGerente) {
            // Para GERENTE: usar a API de regiões responsáveis
            const response = await mainService.getUserRegions(this.currentUser.id);
            
            if (response.data) {
              this.userResponsibleRegions = [];
              
              // Adicionar todas as regiões retornadas pela API
              response.data.forEach(region => {
                if (!this.userResponsibleRegions.includes(region.id)) {
                  this.userResponsibleRegions.push(region.id);
                }
              });
              
              // Adicionar região principal se existir e não estiver na lista
              if (this.currentUser.regionId && !this.userResponsibleRegions.includes(this.currentUser.regionId)) {
                this.userResponsibleRegions.push(this.currentUser.regionId);
              }
            }
          } else if (this.isCobrador) {
            // Para COBRADOR: pode acessar todas as regiões (apenas visualização)
            // Não precisa carregar regiões específicas pois tem acesso global
            this.userResponsibleRegions = [];
          }
        }
      } catch (error) {
        console.error('Erro ao carregar regiões do usuário:', error);
        this.userResponsibleRegions = [];
      }
    },
    
    async loadCurrentRegionData() {
      try {
        const response = await mainService.getRegionById(this.currentRegion.id);
        if (response.data) {
          this.currentRegionData = response.data;
          this.prizeExpirationDays = response.data.prizeExpirationDays || 7;
          // Default true se a API/coluna ainda não existir — não muda o comportamento atual
          this.requirePinForPrizeClaim = response.data.requirePinForPrizeClaim !== false;
          this.milharBonusEnabled = response.data.milharBonusEnabled === true;
          this.milharBonusMinValue = Number(response.data.milharBonusMinValue) || 0;
          this.milharBonusMaxBetValue = Number(response.data.milharBonusMaxBetValue) > 0
            ? Number(response.data.milharBonusMaxBetValue)
            : 10;
          this.milharBonusCotacao = Number(response.data.milharBonusCotacao) > 0
            ? Number(response.data.milharBonusCotacao)
            : 100;
          this.milharBonusMaxPrize = Number(response.data.milharBonusMaxPrize) > 0
            ? Number(response.data.milharBonusMaxPrize)
            : 1000;
          this.ticketFooterEnabled = response.data.ticketFooterEnabled === true;
          this.ticketFooterImage = response.data.ticketFooterImage || null;
          this.ticketFooterText = response.data.ticketFooterText || '';
        }
      } catch (error) {
        console.error('Erro ao carregar dados da região:', error);
        this.prizeExpirationDays = 7;
        this.requirePinForPrizeClaim = true;
        this.milharBonusEnabled = false;
        this.milharBonusMinValue = 0;
        this.milharBonusMaxBetValue = 10;
        this.milharBonusCotacao = 100;
        this.milharBonusMaxPrize = 1000;
        this.ticketFooterEnabled = false;
        this.ticketFooterImage = null;
        this.ticketFooterText = '';
      }
    },

    canAccessCurrentRegion() {
      if (this.isAdmin) {
        return true; // ADMIN tem acesso a todas as regiões
      }
      
      if (this.isCobrador) {
        return true; // COBRADOR pode ver todas as regiões (mas não editar)
      }
      
      if (!this.currentRegion) {
        return false;
      }
      
      // GERENTE e BANCA só podem acessar suas regiões responsáveis  
      return this.userResponsibleRegions.includes(this.currentRegion.id);
    },
    
    preserveAccordionState() {
      // Salvar estado dos accordions no sessionStorage
      const accordionState = {
        openLotteries: this.openLotteries,
        openModalities: this.openModalities,
        openLimits: this.openLimits,
        openExhausts: this.openExhausts,
        openLotteryCotacao: this.openLotteryCotacao,
        openGeneralSettings: this.openGeneralSettings
      };
      sessionStorage.setItem('regionsConfigsAccordionState', JSON.stringify(accordionState));
    },
    
    restoreAccordionState() {
      // Restaurar estado dos accordions do sessionStorage
      const saved = sessionStorage.getItem('regionsConfigsAccordionState');
      if (saved) {
        try {
          const state = JSON.parse(saved);
          this.openLotteries = state.openLotteries ?? true;
          this.openModalities = state.openModalities ?? false;
          this.openLimits = state.openLimits ?? false;
          this.openExhausts = state.openExhausts ?? false;
          this.openLotteryCotacao = state.openLotteryCotacao ?? false;
          this.openGeneralSettings = state.openGeneralSettings ?? false;
        } catch (error) {
          console.warn('Erro ao restaurar estado dos accordions:', error);
        }
      }
    },
    
    searchRegionConfigs(showLoading = true) {
      if (showLoading) {
        this.loading = true;
      }
      mainService
        .searchRegionConfigs()
        .then((response) => {
          this.regionParameter = response.data;
          this.normalizeRegionModalitiesOrder(false);
          this.regionParameter.regionLotteriesParameter.forEach((c) => {
            // Processar maxHourToAcceptBet: manter formato HH:mm para compatibilidade com input type="time"
            if (c.maxHourToAcceptBet) {
              let acc = c.maxHourToAcceptBet.split(".")[0]; // Remove milissegundos se existirem
              acc = acc.split(":")[0] + ":" + acc.split(":")[1]; // Mantém apenas HH:mm
              c.maxHourToAcceptBet = acc;
            }

            // Processar maxHourToCancelBet: manter formato original para o componente personalizado
            if (c.maxHourToCancelBet) {
              // Não alterar o formato aqui - deixar como veio do backend
              // O componente RegionsConfigsLotteries irá lidar com o formato
            }
          });
          this.regionParameter.regionModalitiesParameter.forEach((c) => {
            c.operation = "Update";
            this.normalizeRegionModalityDetailsOrder(c, false);
          });
        })
        .catch((error) => {
          toast.error(handleErrorMessage(error));
        })
        .finally(() => {
          if (showLoading) {
            this.loading = false;
          }
        });
    },
    saveRegionConfigs() {
      // Verificar permissão antes de salvar
      if (!this.canEdit) {
        toast.error("Você não tem permissão para editar as configurações desta região!");
        return;
      }
      
      this.saving = true;
      
      // Preparar dados para o backend - garantir formato correto dos campos de tempo
      this.normalizeRegionModalitiesOrder(true);
      this.normalizeAllRegionModalityDetailsOrder(true);
      const regionParameterToSave = JSON.parse(JSON.stringify(this.regionParameter));
      regionParameterToSave.regionLotteriesParameter.forEach((c) => {
        // maxHourToAcceptBet: já está no formato HH:mm, adicionar segundos
        if (c.maxHourToAcceptBet && !c.maxHourToAcceptBet.includes(":00", 5)) {
          c.maxHourToAcceptBet = c.maxHourToAcceptBet + ":00";
        }
        
        // maxHourToCancelBet: garantir que tenha segundos
        if (c.maxHourToCancelBet && !c.maxHourToCancelBet.includes(":00", 5)) {
          c.maxHourToCancelBet = c.maxHourToCancelBet + ":00";
        }
      });
      
      mainService
        .saveRegionConfigs(regionParameterToSave)
        .then((response) => {
          // Mostrar toast de sucesso
          toast.success("Configurações salvas!", {
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false
          });

          // Resetar estado de saving no componente filho
          if (this.$refs.modalitiesComponent) {
            this.$refs.modalitiesComponent.resetSaving();
          }
          if (this.$refs.lotteriesComponent) {
            this.$refs.lotteriesComponent.resetSaving();
          }

          // Recarregar os dados do backend para sincronizar (sem mostrar loading para não recolher accordions)
          this.searchRegionConfigs(false);
        })
        .catch((error) => {
          toast.error(handleErrorMessage(error));
          // Resetar estado de saving em caso de erro
          if (this.$refs.modalitiesComponent) {
            this.$refs.modalitiesComponent.resetSaving();
          }
          if (this.$refs.lotteriesComponent) {
            this.$refs.lotteriesComponent.resetSaving();
          }
        })
        .finally(() => {
          this.saving = false;
        });
    },
    savePrizeSettings() {
      if (this.prizeExpirationDays < 1 || this.prizeExpirationDays > 365) {
        toast.error("O valor deve estar entre 1 e 365 dias!");
        return;
      }

      if (this.milharBonusMinValue < 0) {
        toast.error("O valor mínimo do milhar de bônus não pode ser negativo!");
        return;
      }
      if (this.milharBonusMaxBetValue <= 0) {
        toast.error("O valor máximo da aposta do milhar de bônus deve ser maior que zero!");
        return;
      }
      if (this.milharBonusCotacao <= 0) {
        toast.error("A cotação do milhar de bônus deve ser maior que zero!");
        return;
      }
      if (this.milharBonusMaxPrize <= 0) {
        toast.error("O prêmio máximo do milhar de bônus deve ser maior que zero!");
        return;
      }

      if (this.prizeSettingsTimeout) {
        clearTimeout(this.prizeSettingsTimeout);
      }
      this.prizeSettingsTimeout = setTimeout(() => {
        this.persistPrizeSettings();
      }, 400);
    },

    async persistPrizeSettings() {
      if (!this.canEdit) {
        toast.error("Você não tem permissão para editar as configurações desta região!");
        return;
      }

      if (!this.currentRegionData) {
        toast.error("Dados da região não carregados!");
        return;
      }

      try {
        // Enviar TODOS os campos do update — bool omitido vira false no binder e pode desativar a região
        const regionData = {
          id: this.currentRegion.id,
          name: this.currentRegionData.name,
          responsibleId: this.currentRegionData.responsibleId,
          descargaRegionId: this.currentRegionData.descargaRegionId,
          bancaId: this.currentRegionData.bancaId,
          onlyDescarga: this.currentRegionData.onlyDescarga === true,
          prizeExpirationDays: parseInt(this.prizeExpirationDays) || 7,
          requirePinForPrizeClaim: this.requirePinForPrizeClaim !== false,
          milharBonusEnabled: this.milharBonusEnabled === true,
          milharBonusMinValue: Number(this.milharBonusMinValue) || 0,
          milharBonusMaxBetValue: Number(this.milharBonusMaxBetValue) > 0
            ? Number(this.milharBonusMaxBetValue)
            : 10,
          milharBonusCotacao: Number(this.milharBonusCotacao) > 0
            ? Number(this.milharBonusCotacao)
            : 100,
          milharBonusMaxPrize: Number(this.milharBonusMaxPrize) > 0
            ? Number(this.milharBonusMaxPrize)
            : 1000,
          ticketFooterEnabled: this.ticketFooterEnabled === true,
          ticketFooterImage: this.ticketFooterImage || null,
          ticketFooterText: (this.ticketFooterText || '').trim() || null,
          isActive: this.currentRegionData.isActive !== false
        };

        await mainService.updateRegion(regionData);

        this.currentRegionData.prizeExpirationDays = regionData.prizeExpirationDays;
        this.currentRegionData.requirePinForPrizeClaim = regionData.requirePinForPrizeClaim;
        this.currentRegionData.milharBonusEnabled = regionData.milharBonusEnabled;
        this.currentRegionData.milharBonusMinValue = regionData.milharBonusMinValue;
        this.currentRegionData.milharBonusMaxBetValue = regionData.milharBonusMaxBetValue;
        this.currentRegionData.milharBonusCotacao = regionData.milharBonusCotacao;
        this.currentRegionData.milharBonusMaxPrize = regionData.milharBonusMaxPrize;
        this.currentRegionData.ticketFooterEnabled = regionData.ticketFooterEnabled;
        this.currentRegionData.ticketFooterImage = regionData.ticketFooterImage;
        this.currentRegionData.ticketFooterText = regionData.ticketFooterText;
        
        toast.success("Configurações de prêmio atualizadas!", {
          autoClose: 3000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: false
        });
      } catch (error) {
        toast.error(handleErrorMessage(error));
      }
    },

    async onTicketFooterImageSelected(event) {
      const file = event?.target?.files?.[0];
      await this.applyTicketFooterImageFile(file);
      if (event?.target) event.target.value = '';
    },

    async onTicketFooterImageDrop(event) {
      if (this.isReadOnly || this.ticketFooterImageLoading) return;
      const file = event?.dataTransfer?.files?.[0];
      await this.applyTicketFooterImageFile(file);
    },

    async applyTicketFooterImageFile(file) {
      if (!file) return;
      this.ticketFooterImageLoading = true;
      try {
        const dataUrl = await this.compressTicketFooterImage(file);
        this.ticketFooterImage = dataUrl;
        this.savePrizeSettings();
        toast.success('Imagem carregada. Prévia atualizada.');
      } catch (err) {
        toast.error(err?.message || 'Não foi possível processar a imagem.');
      } finally {
        this.ticketFooterImageLoading = false;
      }
    },

    clearTicketFooterImage() {
      this.ticketFooterImage = null;
      this.savePrizeSettings();
    },

    compressTicketFooterImage(file) {
      return new Promise((resolve, reject) => {
        if (!file.type?.startsWith('image/')) {
          reject(new Error('Selecione um arquivo de imagem.'));
          return;
        }
        const reader = new FileReader();
        reader.onerror = () => reject(new Error('Falha ao ler a imagem.'));
        reader.onload = () => {
          const img = new Image();
          img.onerror = () => reject(new Error('Imagem inválida.'));
          img.onload = () => {
            const maxW = 400;
            const scale = Math.min(1, maxW / img.width);
            const w = Math.max(1, Math.round(img.width * scale));
            const h = Math.max(1, Math.round(img.height * scale));
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0, w, h);
            let quality = 0.75;
            let dataUrl = canvas.toDataURL('image/jpeg', quality);
            while (dataUrl.length > 400000 && quality > 0.4) {
              quality -= 0.1;
              dataUrl = canvas.toDataURL('image/jpeg', quality);
            }
            if (dataUrl.length > 450000) {
              reject(new Error('Imagem muito grande. Escolha outra menor.'));
              return;
            }
            resolve(dataUrl);
          };
          img.src = reader.result;
        };
        reader.readAsDataURL(file);
      });
    },

    receiveAddModality() {
      // Verificar permissão antes de adicionar modalidade
      if (!this.canEdit) {
        toast.error("Você não tem permissão para adicionar modalidades!");
        return;
      }

      // Apenas 1 modalidade por região
      const visibleCount = (this.regionParameter.regionModalitiesParameter || [])
        .filter(m => m.operation !== "Delete")
        .length;
      if (visibleCount >= 1) {
        toast.warning("Só é permitido cadastrar uma modalidade por região.");
        return;
      }
      
      let modalities = [];
      let regionModalityDetail = [];
      mainService.searchModalities().then((response) => {
        modalities = response.data;
        modalities.forEach((m) => {
          regionModalityDetail.push({
            "modalityId": m.id,
            "modalityName": m.name,
            "maxPrize": 0,
            "price": 0,
            "isActive": false,
            "sortOrder": regionModalityDetail.length + 1,
          });
        });
        let regionModality = {
          "regionId": this.regionParameter.regionId,
          "name": "",
          "sortOrder": this.getNextModalitySortOrder(),
          "operation": "Add",
          "regionModalityDetail": regionModalityDetail,
          // Adicionar ID temporário único para evitar problemas de renderização
          "tempId": `temp-${Date.now()}-${Math.random()}`
        };
        this.regionParameter.regionModalitiesParameter.push(regionModality);
        this.normalizeRegionModalitiesOrder(false);
        this.normalizeRegionModalityDetailsOrder(regionModality, false);
      });
    },

    getNextModalitySortOrder() {
      const visibleModalities = (this.regionParameter.regionModalitiesParameter || [])
        .filter(m => m.operation !== "Delete");
      return visibleModalities.length + 1;
    },

    normalizeRegionModalitiesOrder(markChanged = false) {
      if (!this.regionParameter.regionModalitiesParameter || !Array.isArray(this.regionParameter.regionModalitiesParameter)) {
        return;
      }

      const activeItems = this.regionParameter.regionModalitiesParameter
        .filter(m => m.operation !== "Delete")
        .sort((a, b) => {
          const orderA = Number.isFinite(a.sortOrder) && a.sortOrder > 0 ? a.sortOrder : Number.MAX_SAFE_INTEGER;
          const orderB = Number.isFinite(b.sortOrder) && b.sortOrder > 0 ? b.sortOrder : Number.MAX_SAFE_INTEGER;
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          return (a.name || "").localeCompare(b.name || "");
        });

      const deletedItems = this.regionParameter.regionModalitiesParameter.filter(m => m.operation === "Delete");

      activeItems.forEach((item, index) => {
        const nextOrder = index + 1;
        if (item.sortOrder !== nextOrder) {
          item.sortOrder = nextOrder;
          if (markChanged && item.operation === "Update") {
            item.hasChanges = true;
          }
        }
      });

      this.regionParameter.regionModalitiesParameter = [...activeItems, ...deletedItems];
    },

    normalizeRegionModalityDetailsOrder(modalityParameter, markChanged = false) {
      if (!modalityParameter || !Array.isArray(modalityParameter.regionModalityDetail)) {
        return;
      }

      const orderedDetails = [...modalityParameter.regionModalityDetail].sort((a, b) => {
        const orderA = Number.isFinite(a.sortOrder) && a.sortOrder > 0 ? a.sortOrder : Number.MAX_SAFE_INTEGER;
        const orderB = Number.isFinite(b.sortOrder) && b.sortOrder > 0 ? b.sortOrder : Number.MAX_SAFE_INTEGER;
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        return (a.modalityName || "").localeCompare(b.modalityName || "");
      });

      let changed = false;
      orderedDetails.forEach((detail, index) => {
        const nextOrder = index + 1;
        if (detail.sortOrder !== nextOrder) {
          detail.sortOrder = nextOrder;
          changed = true;
        }
      });

      modalityParameter.regionModalityDetail = orderedDetails;
      if (changed && markChanged && modalityParameter.operation === "Update") {
        modalityParameter.hasChanges = true;
      }
    },

    normalizeAllRegionModalityDetailsOrder(markChanged = false) {
      if (!this.regionParameter.regionModalitiesParameter || !Array.isArray(this.regionParameter.regionModalitiesParameter)) {
        return;
      }

      this.regionParameter.regionModalitiesParameter
        .filter(m => m.operation !== "Delete")
        .forEach(modalityParameter => this.normalizeRegionModalityDetailsOrder(modalityParameter, markChanged));
    },
    
    // Método de auto-salvamento com debounce
    autoSave() {
      // Verificar permissão antes de salvar
      if (!this.canEdit) {
        return;
      }
      
      // Limpar timeout anterior se existir
      if (this.autoSaveTimeout) {
        clearTimeout(this.autoSaveTimeout);
      }
      
      // Criar novo timeout para salvar após 1 segundo de inatividade
      this.autoSaveTimeout = setTimeout(() => {
        this.saveRegionConfigs();
      }, 1000);
    },
  },
  };
  </script>

<style scoped>
.copy-configs-card {
  margin-top: 16px;
  padding: 16px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--card-background);
}

.copy-configs-header {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 14px;
}

.copy-configs-header > i {
  color: var(--primary-color);
  margin-top: 3px;
}

.copy-configs-header strong {
  display: block;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.copy-configs-header p {
  margin: 0;
  color: var(--text-muted);
  font-size: 0.9em;
  line-height: 1.4;
}

.copy-configs-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}

.copy-region-select-wrapper {
  position: relative;
  min-width: 260px;
  max-width: 420px;
  flex: 1;
}

select.copy-region-select.form-input-modern {
  width: 100%;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  color-scheme: light;
  cursor: pointer;
  opacity: 1;
  padding-right: 2.5rem;
  /* Uma única seta (sem ícone FA duplicado / sem repeat) */
  background-color: var(--input-background) !important;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e") !important;
  background-position: right 0.75rem center !important;
  background-repeat: no-repeat !important;
  background-size: 1.25em 1.25em !important;
}

select.copy-region-select.form-input-modern:not(:disabled) {
  opacity: 1 !important;
  cursor: pointer !important;
}

select.copy-region-select.form-input-modern option {
  background: var(--card-background);
  color: var(--text-primary);
}

[data-theme="dark"] select.copy-region-select.form-input-modern {
  color-scheme: dark;
  color: var(--text-primary);
  border-color: var(--border-color);
  background-color: var(--input-background) !important;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23d1d5db' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e") !important;
  background-position: right 0.75rem center !important;
  background-repeat: no-repeat !important;
  background-size: 1.25em 1.25em !important;
}

[data-theme="dark"] select.copy-region-select.form-input-modern option {
  background: var(--card-background);
  color: var(--text-primary);
}

.btn-copy-configs {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 6px;
  background: var(--primary-color);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.btn-copy-configs:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* Evitar que dropdowns de Limites fiquem cortados pelo accordion */
.accordion-modern.limits-open,
.accordion-modern.limits-open :deep(.accordion-collapse.show),
.accordion-modern.limits-open :deep(.accordion-body-modern) {
  overflow: visible;
}

/* Estilos para elementos em modo somente leitura */
.alert {
  padding: 12px 16px;
  border-radius: 6px;
  border: 1px solid;
  display: flex;
  align-items: center;
  gap: 8px;
}

.alert-warning {
  background-color: #fef3cd;
  border-color: #faebcc;
  color: #856404;
}

[data-theme="dark"] .alert-warning {
  background-color: #332701;
  border-color: #664d03;
  color: #ffda6a;
}

/* Estilos para inputs desabilitados */
input:disabled,
input:read-only,
button:disabled,
.disabled {
  opacity: 0.6 !important;
  cursor: not-allowed !important;
}

/* Manter cor verde nos checkboxes desabilitados quando marcados */
input[type="checkbox"]:disabled:checked {
  accent-color: green !important;
  background-color: green !important;
  border-color: green !important;
  opacity: 0.8 !important; /* Levemente menos opaco que outros inputs desabilitados */
}

/* Para navegadores que suportam appearance */
input[type="checkbox"]:disabled:checked {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-color: green !important;
  border: 2px solid green !important;
  border-radius: 3px;
  width: 20px;
  height: 20px;
  position: relative;
  cursor: not-allowed;
}

/* Adicionar o checkmark ✓ nos checkboxes desabilitados marcados */
input[type="checkbox"]:disabled:checked::after {
  content: '✓';
  position: absolute;
  color: white;
  font-size: 14px;
  font-weight: bold;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  line-height: 1;
}

.fa-plus.disabled {
  color: #6c757d !important;
  cursor: not-allowed !important;
  opacity: 0.5 !important;
}

.fa-plus.disabled:hover {
  color: #6c757d !important;
}

/* Checkboxes no tema escuro */
[data-theme="dark"] input[type="checkbox"]:disabled:checked {
  background-color: #28a745 !important; /* Verde mais claro para tema escuro */
  border-color: #28a745 !important;
  accent-color: #28a745 !important;
}

[data-theme="dark"] input[type="checkbox"]:disabled:checked::after {
  color: white;
}

/* Estilos para Configurações Gerais */
.general-settings-container {
  padding: 20px;
}

.setting-item {
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
}

.setting-header {
  margin-bottom: 16px;
}

.setting-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.1em;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.setting-label i {
  color: var(--primary-color);
}

.setting-description {
  color: var(--text-muted);
  font-size: 0.9em;
  line-height: 1.4;
  display:flex;
}

.setting-input {
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 300px;
}

.form-input-modern {
  flex: 1;
  padding: 10px 14px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  font-size: 1em;
  background: var(--input-background);
  color: var(--text-primary);
  transition: all 0.2s ease;
}

.form-input-modern:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px var(--primary-color-10);
}

/* :read-only também casa com <select>; limitar a inputs de texto */
input.form-input-modern:disabled,
input.form-input-modern:read-only {
  opacity: 0.6;
  cursor: not-allowed;
  background: var(--disabled-background);
}

.input-suffix {
  font-weight: 500;
  color: var(--text-secondary);
  white-space: nowrap;
}

.setting-toggle {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.setting-controls {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.milhar-bonus-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px 24px;
}

.milhar-bonus-fields {
  display: flex;
  flex-wrap: nowrap;
  align-items: flex-end;
  gap: 10px;
  margin-top: 14px;
  width: 100%;
}

.milhar-field-label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 0;
  min-width: 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary, #a1a1aa);
}

.milhar-field-input {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--input-background);
  color: var(--text-primary);
  font-size: 13px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.milhar-field-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px var(--primary-color-10);
}

.ticket-footer-fields {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 14px;
  width: 100%;
}

.ticket-footer-image-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ticket-footer-field-title {
  font-size: 12px;
  font-weight: 700;
  color: var(--text-secondary, #a1a1aa);
}

.ticket-footer-dropzone {
  width: 100%;
  max-width: 360px;
  min-height: 160px;
  border: 2px dashed var(--border-color);
  border-radius: 10px;
  background: var(--input-background, #111);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
  padding: 10px;
  box-sizing: border-box;
}

.ticket-footer-dropzone:hover:not(.disabled) {
  border-color: var(--primary-color);
}

.ticket-footer-dropzone.has-image {
  border-style: solid;
  background: #fff;
}

.ticket-footer-dropzone.disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.ticket-footer-dropzone.loading {
  cursor: wait;
}

.ticket-footer-preview-img {
  display: block;
  width: 100%;
  max-height: 220px;
  object-fit: contain;
}

.ticket-footer-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  color: var(--text-secondary, #a1a1aa);
  padding: 16px;
}

.ticket-footer-placeholder i {
  font-size: 28px;
  color: var(--primary-color, #0288d1);
}

.ticket-footer-placeholder span {
  font-size: 13px;
  font-weight: 600;
}

.ticket-footer-placeholder small {
  font-size: 11px;
  opacity: 0.85;
}

.ticket-footer-file-input {
  display: none;
}

.ticket-footer-image-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.ticket-footer-upload-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid var(--border-color);
  background: var(--input-background);
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.ticket-footer-upload-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ticket-footer-remove-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #ef4444;
  background: transparent;
  color: #ef4444;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.ticket-footer-remove-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ticket-footer-hint {
  color: var(--text-secondary, #a1a1aa);
  font-size: 11px;
}

.ticket-footer-text-label {
  max-width: 480px;
}

.milhar-field-input:disabled,
.milhar-field-input:read-only {
  opacity: 0.6;
  cursor: not-allowed;
  background: var(--disabled-background);
}

@media (max-width: 900px) {
  .milhar-bonus-fields {
    flex-wrap: wrap;
  }

  .milhar-field-label {
    flex: 1 1 calc(50% - 10px);
    min-width: 140px;
  }
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 52px;
  height: 28px;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: #ccc;
  transition: 0.2s;
  border-radius: 28px;
}

.toggle-slider::before {
  position: absolute;
  content: "";
  height: 22px;
  width: 22px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.2s;
  border-radius: 50%;
}

.toggle-switch input:checked + .toggle-slider {
  background-color: var(--primary-color, #28a745);
}

.toggle-switch input:checked + .toggle-slider::before {
  transform: translateX(24px);
}

.toggle-switch.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.toggle-switch.disabled .toggle-slider {
  cursor: not-allowed;
}

.toggle-status {
  font-size: 0.95em;
  font-weight: 500;
  color: var(--text-primary);
}
</style>

  